// Public documentation site — chitdesk.com/docs. Standalone, Shell-free, in the
// landing's editorial aesthetic (.lp canvas, Fraunces display, forest green).
// Master-detail over the bilingual content in lib/docs-content.ts: searchable,
// section-grouped nav + an article reader. Deep-linkable via /docs/<slug>.

import { useEffect, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { use_language, set_language, font_class_for } from '../i18n/index.js';
import { master_app_url } from '../lib/marketing.js';
import {
  DOCS_SECTIONS,
  DOCS_ARTICLES,
  type DocsArticle,
  type DocsBlock,
} from '../lib/docs-content.js';
import './landing.css';

function BrandMark({ size = 32 }: { size?: number }): ReactElement {
  return (
    <span
      className="inline-grid place-items-center rounded-[0.4em] bg-primary text-white font-tamil leading-none shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.5 }}
      aria-hidden
    >
      சீ
    </span>
  );
}

function Block({ block, ta }: { block: DocsBlock; ta: boolean }): ReactElement {
  const tf = ta ? 'font-tamil' : '';
  switch (block.kind) {
    case 'heading':
      return <h3 className={`text-lg font-semibold text-primary-ink mt-8 ${ta ? 'font-tamil' : 'lp-display'}`}>{ta ? block.ta : block.en}</h3>;
    case 'p':
      return <p className={`text-[15px] leading-relaxed text-ink-2 ${tf}`}>{ta ? block.ta : block.en}</p>;
    case 'list':
      return (
        <ul className="space-y-2">
          {(ta ? block.ta : block.en).map((s, i) => (
            <li key={i} className={`flex gap-2.5 text-[15px] leading-relaxed text-ink-2 ${tf}`}>
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="space-y-2.5">
          {(ta ? block.ta : block.en).map((s, i) => (
            <li key={i} className={`flex gap-3 text-[15px] leading-relaxed text-ink-2 ${tf}`}>
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary-soft text-primary text-xs font-semibold grid place-items-center">{i + 1}</span>
              <span className="pt-0.5">{s}</span>
            </li>
          ))}
        </ol>
      );
    case 'tip':
      return (
        <div className={`rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-[14px] leading-relaxed text-ink-2 ${tf}`}>
          <span className="font-medium text-primary">{ta ? 'குறிப்பு · ' : 'Tip · '}</span>
          {ta ? block.ta : block.en}
        </div>
      );
  }
}

export function DocsPage(): ReactElement {
  const lang = use_language();
  const ta = lang === 'ta';
  const tf = font_class_for(lang);
  const t = (en: string, tam: string): string => (ta ? tam : en);
  const dsp = ta ? 'font-tamil' : 'lp-display';

  const { slug } = useParams();
  const active: DocsArticle = DOCS_ARTICLES.find((a) => a.slug === slug) ?? DOCS_ARTICLES[0]!;
  const [query, set_query] = useState('');

  // Match query against title + summary + paragraph/heading/tip text, both langs.
  const matches = (a: DocsArticle): boolean => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      a.title_en, a.title_ta, a.summary_en, a.summary_ta,
      ...a.body.flatMap((b) => (b.kind === 'list' || b.kind === 'steps' ? [...b.en, ...b.ta] : [b.en, b.ta])),
    ].join(' ').toLowerCase();
    return hay.includes(q);
  };

  // Per-article tab title (SEO + the browser tab); scroll to top on change.
  useEffect(() => {
    document.title = `${active.title_en} — ChitDesk Docs`;
    window.scrollTo({ top: 0 });
  }, [active.slug, active.title_en]);

  // Search filters titles + summaries + paragraph/heading text, current-language.
  const sidebar_nav = (on_click?: () => void): ReactElement => (
    <nav className="space-y-6">
      <input
        type="search"
        value={query}
        onChange={(e) => set_query(e.target.value)}
        placeholder={t('Search the docs…', 'ஆவணங்களில் தேடுங்கள்…')}
        className={`w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary ${tf}`}
      />
      {DOCS_SECTIONS.map((sec) => {
        const arts = DOCS_ARTICLES.filter((a) => a.section === sec.key && matches(a));
        if (arts.length === 0) return null;
        return (
          <div key={sec.key}>
            <div className={`lp-eyebrow text-ink-3 mb-2`} style={{ fontSize: 10 }}>{ta ? sec.title_ta : sec.title_en}</div>
            <ul className="space-y-0.5">
              {arts.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/docs/${a.slug}`}
                    onClick={on_click}
                    className={`block px-3 py-1.5 rounded-lg text-sm transition no-underline ${tf} ${a.slug === active.slug ? 'bg-primary/10 text-primary font-medium' : 'text-ink-2 hover:text-primary hover:bg-bg-elev'}`}
                  >
                    {ta ? a.title_ta : a.title_en}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {query.trim() && !DOCS_ARTICLES.some(matches) && (
        <p className={`px-3 text-sm text-ink-3 ${tf}`}>{t('No matching topics.', 'பொருந்தும் தலைப்புகள் இல்லை.')}</p>
      )}
    </nav>
  );

  const signin_url = master_app_url('/login');

  return (
    <main className="lp lp-canvas lp-grain min-h-screen text-ink antialiased overflow-x-hidden">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-40 border-b border-line/70" style={{ background: 'rgba(246,242,236,0.72)', backdropFilter: 'saturate(140%) blur(10px)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 sm:px-6 h-16">
          <a href="/" className="flex items-center gap-2.5 no-underline">
            <BrandMark size={34} />
            <span className="flex flex-col leading-none">
              <span className="text-lg font-semibold tracking-tight text-primary-ink">ChitDesk</span>
              <span className="lp-eyebrow text-ink-3" style={{ fontSize: 9 }}>{t('Documentation', 'ஆவணங்கள்')}</span>
            </span>
          </a>
          <nav className="flex items-center gap-1 sm:gap-2">
            <a href="/#pricing" className={`hidden sm:inline-block px-3.5 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition no-underline ${tf}`}>{t('Pricing', 'கட்டணம்')}</a>
            <button type="button" onClick={() => set_language(ta ? 'en' : 'ta')} className="px-3 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition">{ta ? 'English' : 'தமிழ்'}</button>
            <a href={signin_url} className={`lp-cta px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white no-underline ${tf}`}>{t('Open app', 'செயலியைத் திற')}</a>
          </nav>
        </div>
      </header>

      {/* ── Heading ── */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-12 pb-6">
        <div className="lp-eyebrow text-primary mb-3">{t('Documentation', 'ஆவணங்கள்')}</div>
        <h1 className={`${dsp} text-3xl sm:text-4xl text-primary-ink`} style={{ fontWeight: 600 }}>{t('Everything you can do with ChitDesk', 'ChitDesk-இல் நீங்கள் செய்யக்கூடிய அனைத்தும்')}</h1>
        <p className={`mt-3 text-ink-2 max-w-2xl ${tf}`}>{t('Plain, step-by-step guides for every feature — for operators and members.', 'ஒவ்வொரு அம்சத்திற்கும் எளிய, படிப்படியான வழிகாட்டுதல்கள் — நிறுவனர் + உறுப்பினர்களுக்கு.')}</p>
      </section>

      {/* ── Body ── */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pb-24 grid gap-8 lg:grid-cols-[250px_1fr] items-start">
        {/* Mobile: collapsible topic list */}
        <details className="lg:hidden rounded-xl border border-line bg-surface px-4 py-3">
          <summary className={`cursor-pointer text-sm font-medium text-primary-ink ${tf}`}>{t('Browse all topics', 'அனைத்து தலைப்புகளையும் காண')}</summary>
          <div className="mt-4">{sidebar_nav(() => { /* native nav handles close on link */ })}</div>
        </details>

        {/* Desktop sidebar */}
        <aside className="hidden lg:block lg:sticky lg:top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2">
          {sidebar_nav()}
        </aside>

        {/* Article */}
        <article className="min-w-0">
          <div className="rounded-2xl border border-line bg-surface p-7 sm:p-9 max-w-2xl">
            <h2 className={`text-2xl text-primary-ink ${dsp}`} style={{ fontWeight: 600 }}>{ta ? active.title_ta : active.title_en}</h2>
            <p className={`mt-2 text-[15px] text-ink-3 ${tf}`}>{ta ? active.summary_ta : active.summary_en}</p>
            <div className="mt-7 space-y-4">
              {active.body.map((block, i) => (
                <Block key={i} block={block} ta={ta} />
              ))}
            </div>
          </div>

          {/* Help nudge */}
          <div className={`mt-6 text-sm text-ink-3 max-w-2xl ${tf}`}>
            {t('Need a hand? Open the app and use the in-app help, or ', 'உதவி வேண்டுமா? செயலியைத் திறந்து உள்-செயலி உதவியைப் பயன்படுத்துங்கள், அல்லது ')}
            <a href={signin_url} className="text-primary hover:underline no-underline">{t('sign in', 'உள்நுழையுங்கள்')}</a>.
          </div>
        </article>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-2.5">
            <BrandMark size={28} />
            <span className="text-base font-semibold tracking-tight text-primary-ink">ChitDesk</span>
          </div>
          <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-2 justify-center ${tf}`}>
            <a href="/" className="hover:text-primary transition no-underline">{t('Home', 'முகப்பு')}</a>
            <a href="/#pricing" className="hover:text-primary transition no-underline">{t('Pricing', 'கட்டணம்')}</a>
            <a href="/terms" className="hover:text-primary transition no-underline">{t('Terms', 'விதிமுறைகள்')}</a>
            <a href="/privacy" className="hover:text-primary transition no-underline">{t('Privacy', 'தனியுரிமை')}</a>
            <a href="/refund" className="hover:text-primary transition no-underline">{t('Refunds', 'பணத்திரும்பல்')}</a>
            <a href="/contact" className="hover:text-primary transition no-underline">{t('Contact', 'தொடர்பு')}</a>
            <a href={signin_url} className="hover:text-primary transition no-underline">{t('Open app', 'செயலியைத் திற')}</a>
          </div>
          <div className="flex items-center gap-3 text-xs text-ink-3">
            <span>© {new Date().getFullYear()} ChitDesk</span>
            <span className="font-tamil">தமிழ்நாட்டிற்காக கட்டப்பட்டது</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
